import { useMemo } from "react";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { useGetModelsByModelIdQuery } from "infra/api/generated/api";

import { skipToken } from "@reduxjs/toolkit/query";

export type CardActionsVideoCustomProps = {
  _never?: never;
};

export type CardActionsVideoProps =
  FmImageCardActionsProps<CardActionsVideoCustomProps>;

export default function CardActionsVideo({
  asset,
  top,
  bottom,
}: CardActionsVideoProps) {
  const { selectedProject } = useTeamContext();
  const assetId = asset?.id;
  const modelId = asset?.metadata.modelId;
  const firstFrameId = asset?.firstFrame?.assetId;
  const lastFrameId = asset?.lastFrame?.assetId;
  const prompt = asset?.metadata.prompt;

  const shouldFetchModel = asset?.metadata.type === "video2video";
  const { data: modelData, isLoading: isLoadingModel } =
    useGetModelsByModelIdQuery(
      shouldFetchModel && modelId
        ? { modelId, projectId: selectedProject.id }
        : skipToken
    );

  const isVideoUpscaleModel = useMemo(() => {
    return modelData?.model?.tags?.includes("video-upscale");
  }, [modelData?.model?.tags]);

  if (!modelId && !firstFrameId && !lastFrameId && !prompt) {
    return null;
  }

  return (
    <>
      {top}

      {modelId && (
        <MenuItem
          w="170px"
          iconId="Nav/Model/Outline"
          isLoading={isLoadingModel}
          iconH="12px"
          internalLink={
            isLoadingModel
              ? undefined
              : {
                  pathname: isVideoUpscaleModel
                    ? "/videos/enhance"
                    : "/videos/new",
                  query: {
                    modelId,
                  },
                }
          }
          text="Model"
        />
      )}

      {firstFrameId && (
        <MenuItem
          w="170px"
          iconId="Nav/Image/Outline"
          iconH="12px"
          internalLink={{
            pathname: "/videos/new",
            query: {
              importAssetId: firstFrameId,
            },
          }}
          text="First Frame"
        />
      )}

      {lastFrameId && (
        <MenuItem
          w="170px"
          iconId="Nav/Image/Outline"
          iconH="12px"
          internalLink={{
            pathname: "/videos/new",
            query: {
              importAssetId: lastFrameId,
            },
          }}
          text="Last Frame"
        />
      )}

      {!!prompt && (
        <MenuItem
          w="170px"
          iconId="Ui/Prompt"
          iconH="12px"
          internalLink={{
            pathname: "/videos/new",
            query: {
              importPromptFromAssetId: assetId,
            },
          }}
          text="Prompt"
        />
      )}

      {bottom}
    </>
  );
}
